import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useOutletContext } from 'react-router-dom';
import { useConfirm } from '../../ConfirmDialog';
import { downloadFile } from '../../../lib/assets';
import { fmtDate } from '../../../lib/when';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const DOC_LABEL = { invoice: 'Invoice', warranty: 'Warranty', handover: 'Hand-over form', photo: 'Photo', other: 'Other' };

/** A small glyph for the kind of file, by its name. */
function FileIcon({ name }) {
  const ext = String(name || '').split('.').pop().toLowerCase();
  const kind = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic'].includes(ext) ? 'image'
    : ext === 'pdf' ? 'pdf' : ['xls', 'xlsx', 'csv'].includes(ext) ? 'sheet' : 'doc';
  return (
    <svg className="as-file-icon" data-kind={kind} viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"
      fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 1.5h5.5L13 5v9.5H4z" />
      <path d="M9.5 1.5V5H13" />
      {kind === 'image' && <path d="M5.5 12l2.2-2.6 1.6 1.8 1.2-1.3L12 12z" />}
      {kind === 'pdf' && <path d="M6 12V8.5h1.4a1.2 1.2 0 010 2.4H6M9.8 8.5v3.5" />}
      {kind === 'sheet' && <path d="M5.5 8.5h6M5.5 10.5h6M5.5 12.5h6M8.5 8.5v4" />}
      {kind === 'doc' && <path d="M5.5 8.5h6M5.5 10.5h6M5.5 12.5h4" />}
    </svg>
  );
}

/** FILES: the paperwork (migration 042). Not a contractor's tab. */
export default function FilesTab() {
  const { asset, write, setCount } = useOutletContext();
  return (
    <div className="as-tab as-tab-files">
      <DeviceDocuments asset={asset} write={write} onCount={(n) => setCount('files', n)} />
    </div>
  );
}

/**
 * The paperwork. One file can be attached to several devices at once - the
 * invoice for the ten laptops bought together - and removing it from one
 * device keeps it on the others. Moved from pages/AssetDetail.js in the
 * page redesign; the list gained a glyph for the kind of file.
 */
export function DeviceDocuments({ asset, write, onCount = () => {} }) {
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState('');
  const [confirm, confirmDialog] = useConfirm();
  const [file, setFile] = useState(null);
  const [type, setType] = useState('invoice');
  const [also, setAlso] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API_BASE}/assets/${asset.id}/documents`)
      .then((r) => { setDocs(r.data); setError(''); onCount(Array.isArray(r.data) ? r.data.length : 0); })
      .catch((e) => {
        setDocs([]);
        if (e.response?.status === 503) setError(e.response.data.error);
      });
  }, [asset.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    const formEl = e.target;
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      // Other devices by tag, which is what is printed on them. The server
      // wants ids, so each tag is looked up first, and one it cannot find is
      // named rather than silently dropped.
      const tags = also.split(/[\s,]+/).map((t) => t.trim()).filter(Boolean);
      const ids = [];
      for (const t of tags) {
        const r = await axios.get(`${API_BASE}/assets/by-tag/${encodeURIComponent(t)}`).catch(() => null);
        if (!r?.data?.id) throw new Error(`No device has the tag ${t}.`);
        ids.push(r.data.id);
      }
      const body = new FormData();
      body.append('file', file);
      body.append('doc_type', type);
      if (ids.length) body.append('also', ids.join(','));
      await axios.post(`${API_BASE}/assets/${asset.id}/documents`, body,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setFile(null);
      setAlso('');
      formEl.reset();
      load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not upload that file.');
    } finally { setBusy(false); }
  };

  const remove = async (d) => {
    setError('');
    if (!(await confirm({ title: `Remove ${d.file_name}?`,
      message: d.device_count > 1
        ? `It comes off this device only; it stays on the ${d.device_count - 1} other${d.device_count === 2 ? '' : 's'} it is on.`
        : 'The file is deleted; it cannot be brought back from here.',
      confirmLabel: 'Remove', danger: true }))) return;
    try { await axios.delete(`${API_BASE}/assets/${asset.id}/documents/${d.id}`); load(); } catch (err) {
      setError(err.response?.data?.error || 'Could not remove it.');
    }
  };

  return (
    <section className="as-panel">
      <h2>{`Documents${docs ? ` (${docs.length})` : ''}`}</h2>
      {error && <p className="as-error" role="alert">{error}</p>}
      {docs && docs.length === 0 && !error && (
        <p className="as-empty">No invoice, warranty or hand-over form yet.{write ? ' Add one below.' : ''}</p>
      )}
      {docs && docs.length > 0 && (
        <ul className="as-history as-files">
          {docs.map((d) => (
            <li key={d.id}>
              <FileIcon name={d.file_name} />
              <button type="button" className="btn btn-plain btn-small"
                onClick={() => downloadFile(`/asset-documents/${d.id}/download`, d.file_name)
                  .catch(() => setError('Could not download that file.'))}>
                {d.file_name}
              </button>
              <span className="as-chip">{DOC_LABEL[d.doc_type] || d.doc_type}</span>
              {d.device_count > 1 && (
                <span className="as-quiet">{` also on ${d.device_count - 1} other device${d.device_count === 2 ? '' : 's'}`}</span>
              )}
              {write && (
                <button type="button" className="btn btn-plain btn-small" onClick={() => remove(d)}>Remove</button>
              )}
              <span className="as-when">
                {`Added by ${d.uploaded_by_name || 'someone'} · ${fmtDate(d.created_at)} · ${Math.max(1, Math.round(d.byte_size / 1024))} KB`}
              </span>
            </li>
          ))}
        </ul>
      )}
      {write && (
        <form className="as-inline-form" onSubmit={upload}>
          <div className="form-group">
            <label htmlFor="as-doc-file">File (up to 10 MB)</label>
            <input id="as-doc-file" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
          <div className="form-group" style={{ flex: '0 1 10rem' }}>
            <label htmlFor="as-doc-type">Kind</label>
            <select id="as-doc-type" value={type} onChange={(e) => setType(e.target.value)}>
              {Object.entries(DOC_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="as-doc-also">Also for (tags, optional)</label>
            <input id="as-doc-also" value={also} onChange={(e) => setAlso(e.target.value)}
              placeholder="IT-LAP-000002, IT-LAP-000003" />
          </div>
          <button type="submit" className="btn btn-secondary" disabled={!file || busy}>
            {busy ? 'Uploading…' : 'Upload'}
          </button>
        </form>
      )}
      {confirmDialog}
    </section>
  );
}
